import React, { useEffect, useState, useCallback } from 'react';
import { 
  Box, 
  Button, 
  Grid, 
  TextField, 
  Typography, 
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Person as PersonIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import MessageButton from '../components/MessageButton';
import { API_BASE_URL } from '../utils/api';

const API_BASE = API_BASE_URL;

interface TransferRequest {
  id: number;
  profession: string;
  currentLocation: string;
  targetLocations: string[];
  notes?: string;
  status: string;
  createdAt: string;
  User?: {
    id: number;
    name: string;
    email: string;
    EmployeeProfile?: {
      id: number;
    };
  };
}

const TransferHubRequests: React.FC = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ 
    profession: '', 
    currentLocation: '', 
    targetLocations: '', 
    notes: ''
  });
  const [requests, setRequests] = useState<TransferRequest[]>([]);
  const [editingRequest, setEditingRequest] = useState<TransferRequest | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [message, setMessage] = useState<string>('');

  const load = useCallback(async () => {
    const res = await fetch(`${API_BASE}/transferhub/requests`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) setRequests(await res.json());
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const payload = {
      profession: form.profession,
      currentLocation: form.currentLocation,
      targetLocations: form.targetLocations.split(',').map(s => s.trim()).filter(Boolean),
      notes: form.notes
    };
    const res = await fetch(`${API_BASE}/transferhub/requests`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload)
    });
    if (res.ok) {
      setForm({ profession: '', currentLocation: '', targetLocations: '', notes: '' });
      setMessage('Transfer request created successfully!');
      await load();
    } else {
      setMessage('Failed to create transfer request');
    }
  };

  const update = async () => {
    if (!editingRequest) return;
    
    const payload = {
      profession: form.profession,
      currentLocation: form.currentLocation,
      targetLocations: form.targetLocations.split(',').map(s => s.trim()).filter(Boolean),
      notes: form.notes
    };
    
    const res = await fetch(`${API_BASE}/transferhub/requests/${editingRequest.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload)
    });
    
    if (res.ok) {
      setMessage('Transfer request updated successfully!');
      setOpenDialog(false);
      setEditingRequest(null);
      await load();
    } else {
      setMessage('Failed to update transfer request');
    }
  };

  const deleteRequest = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this transfer request?')) return;
    
    const res = await fetch(`${API_BASE}/transferhub/requests/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });
    
    if (res.ok) {
      setMessage('Transfer request deleted successfully!');
      await load();
    } else {
      setMessage('Failed to delete transfer request');
    }
  };

  const handleEdit = (request: TransferRequest) => {
    setEditingRequest(request);
    setForm({
      profession: request.profession,
      currentLocation: request.currentLocation,
      targetLocations: request.targetLocations.join(', '),
      notes: request.notes || ''
    });
    setOpenDialog(true);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Transfer Requests</Typography>
      
      {message && <Alert sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      {/* Create New Request Form */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Create New Transfer Request</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Profession" 
                fullWidth 
                value={form.profession} 
                onChange={(e) => setForm({ ...form, profession: e.target.value })} 
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Current Location" 
                fullWidth 
                value={form.currentLocation} 
                onChange={(e) => setForm({ ...form, currentLocation: e.target.value })} 
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Target Locations (comma separated)" 
                fullWidth 
                value={form.targetLocations} 
                onChange={(e) => setForm({ ...form, targetLocations: e.target.value })} 
              />
            </Grid>
            <Grid item xs={12}>
              <TextField 
                label="Notes" 
                fullWidth 
                multiline
                rows={3}
                value={form.notes} 
                onChange={(e) => setForm({ ...form, notes: e.target.value })} 
              />
            </Grid>
          </Grid>
          <Button sx={{ mt: 2 }} variant="contained" onClick={create}>Create Request</Button>
        </CardContent>
      </Card>

      {/* All Transfer Requests */}
      <Typography variant="h6" gutterBottom>All Transfer Requests</Typography>
      <Grid container spacing={2}>
        {requests.map(request => (
          <Grid item xs={12} md={6} key={request.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="h6">
                        {request.User?.name || 'Anonymous'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {request.User?.EmployeeProfile?.id && (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => navigate(`/transferhub/profiles/${request.User?.EmployeeProfile?.id}`)}
                      >
                        View Profile
                      </Button>
                    )}
                    {request.User?.email !== user?.email && request.User?.id && (
                      <Box component="span" sx={{ mr: 1 }}>
                        <MessageButton
                          receiverId={request.User.id}
                          receiverName={request.User.name}
                          variant="outlined"
                          size="small"
                          startIcon
                        />
                      </Box>
                    )}
                    {request.User?.email === user?.email && (
                      <>
                        <IconButton onClick={() => handleEdit(request)} size="small">
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => deleteRequest(request.id)} size="small" color="error">
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </Box>
                
                <Typography variant="subtitle1" color="primary" gutterBottom>
                  {request.profession}
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Chip label={request.currentLocation} size="small" sx={{ mr: 1, mb: 1 }} />
                  <Typography variant="body2" color="text.secondary" component="span">
                    → {request.targetLocations.join(', ')}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Chip 
                    label={request.status} 
                    size="small" 
                    color={request.status === 'open' ? 'success' : 'default'}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {new Date(request.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>
                
                {request.notes && (
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                    "{request.notes}"
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Transfer Request</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Profession" 
                fullWidth 
                value={form.profession} 
                onChange={(e) => setForm({ ...form, profession: e.target.value })} 
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Current Location" 
                fullWidth 
                value={form.currentLocation} 
                onChange={(e) => setForm({ ...form, currentLocation: e.target.value })} 
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Target Locations (comma separated)" 
                fullWidth 
                value={form.targetLocations} 
                onChange={(e) => setForm({ ...form, targetLocations: e.target.value })} 
              />
            </Grid>
            <Grid item xs={12}>
              <TextField 
                label="Notes" 
                fullWidth 
                multiline
                rows={3}
                value={form.notes} 
                onChange={(e) => setForm({ ...form, notes: e.target.value })} 
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={update} variant="contained">Update Request</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TransferHubRequests;